import React, { useState } from 'react';
import {
  FileText,
  BarChart3,
  DollarSign,
  Users,
  Clock,
  Calendar,
  TrendingUp,
  Eye,
  Search,
  Filter,
  CheckCircle,
  Briefcase,
  FileSpreadsheet
} from 'lucide-react';

interface ReportSelectorProps {
  onViewReport: (reportType: string) => void;
}

export const ReportSelector: React.FC<ReportSelectorProps> = ({
  onViewReport
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const reportTypes = [
    {
      id: 'financial',
      label: 'Financial Report',
      description: 'Revenue, expenses, profit/loss analysis with real-time data',
      icon: DollarSign,
      category: 'financial',
      color: 'bg-green-50 border-green-200 hover:bg-green-100',
      iconColor: 'text-green-600'
    },
    {
      id: 'task-performance',
      label: 'Task Performance Report',
      description: 'Task completion rates, status distribution, and productivity metrics',
      icon: CheckCircle,
      category: 'operations',
      color: 'bg-blue-50 border-blue-200 hover:bg-blue-100',
      iconColor: 'text-blue-600'
    },
    {
      id: 'time-tracking',
      label: 'Time Tracking Report',
      description: 'Staff time entries, billable hours, and utilization rates',
      icon: Clock,
      category: 'operations',
      color: 'bg-purple-50 border-purple-200 hover:bg-purple-100',
      iconColor: 'text-purple-600'
    },
    {
      id: 'attendance',
      label: 'Attendance Report',
      description: 'Staff attendance patterns, working hours, and leave analysis',
      icon: Users,
      category: 'hr',
      color: 'bg-orange-50 border-orange-200 hover:bg-orange-100',
      iconColor: 'text-orange-600'
    },
    {
      id: 'client-activity',
      label: 'Client Activity Report',
      description: 'Client engagement, project status, and billing activity',
      icon: TrendingUp,
      category: 'client',
      color: 'bg-indigo-50 border-indigo-200 hover:bg-indigo-100',
      iconColor: 'text-indigo-600'
    },
    {
      id: 'payroll',
      label: 'Payroll Report',
      description: 'Salary calculations, deductions, and payroll summaries',
      icon: Briefcase,
      category: 'hr',
      color: 'bg-teal-50 border-teal-200 hover:bg-teal-100',
      iconColor: 'text-teal-600'
    },
    {
      id: 'comprehensive',
      label: 'Comprehensive Report',
      description: 'Complete business overview with all key metrics and insights',
      icon: FileSpreadsheet,
      category: 'executive',
      color: 'bg-gray-50 border-gray-200 hover:bg-gray-100',
      iconColor: 'text-gray-600'
    }
  ];

  const categories = [
    { id: 'all', label: 'All Categories' },
    { id: 'financial', label: 'Financial' },
    { id: 'operations', label: 'Operations' },
    { id: 'hr', label: 'Human Resources' },
    { id: 'client', label: 'Client Management' },
    { id: 'executive', label: 'Executive' }
  ];

  const filteredReports = reportTypes.filter(report => {
    const matchesSearch = report.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         report.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || report.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleViewReport = (reportType: string) => {
    onViewReport(reportType);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Report Viewer</h2>
          <p className="text-gray-600 mt-1">View live reports with current data from the database</p>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search reports..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
          >
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Report Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredReports.map(report => {
          const IconComponent = report.icon;
          return (
            <div
              key={report.id}
              className={`p-6 rounded-lg border-2 transition-all duration-200 cursor-pointer ${report.color}`}
              onClick={() => handleViewReport(report.id)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`p-3 rounded-lg ${report.color.replace('50', '100')}`}>
                  <IconComponent className={`w-6 h-6 ${report.iconColor}`} />
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewReport(report.id);
                  }}
                  className="flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  View Report
                </button>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {report.label}
              </h3>
              
              <p className="text-sm text-gray-600 mb-4">
                {report.description}
              </p>
              
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span className="px-2 py-1 bg-white rounded-full border">
                  Live Data
                </span>
                <span className="flex items-center">
                  <Calendar className="w-3 h-3 mr-1" />
                  Current
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {filteredReports.length === 0 && (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No reports found</h3>
          <p className="text-gray-600">
            Try adjusting your search terms or category filter.
          </p>
        </div>
      )}

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <BarChart3 className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-blue-900 mb-1">Live Data Reports</h4>
            <p className="text-sm text-blue-700">
              These reports fetch current data directly from the database when you click "View Report". 
              No pre-generated reports are used - you'll always see the most up-to-date information.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
